import React, { useEffect, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Modal, Radio, Select } from 'antd';
import styled from '@emotion/styled/macro';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import dayjs from 'dayjs';
import useProjectOptions from '../../hooks/useProjectOptions';
import { FieldBox, FlexWrapper, GreyText, Title } from '../../theme/common_style';
import { createReportApi, updateReportApi } from '../../services/api_collection';
import { DropdownIconNew, LmsIcon } from '../../theme/SvgIcons';
import { apiModules, reportingDonePoints, ReportingType } from '../../utils/constant';
import useGetProjectReport from '../../hooks/useGetProjectReport';
import { convertToHoursAndMinutes, convertToMinutes } from '../../utils/common_functions';
import { useResponsiveModalWidth } from '../../hooks/useResponsiveModalWidth';

const CreateReport = ({ open, onCancel, editDetails, handleList }) => {
  const [form] = Form.useForm();
  const width = useResponsiveModalWidth(800);
  const { user_details } = useSelector((state) => state?.userInfo?.data);
  const { options, loading: projectLoading } = useProjectOptions(user_details?.id);
  const [loading, setLoading] = useState(false);
  const quillRef = React.useRef(null);
  const todaySubmittedReport = useGetProjectReport(
    apiModules.PROJECT,
    user_details?.id,
    !editDetails && form.getFieldValue('reporting_date')
  )?.options;

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      let payload = {
        ...values,
        project_id: +values?.project_id,
        emp_id: user_details?.id,
        reporting_type: ReportingType.REGULAR
      };

      if (payload?.billable) {
        payload.billable_mins = convertToMinutes(payload?.billable_hours, payload?.billable_mins);
      }

      if (values?.reporting_date)
        payload.reporting_date = dayjs(values?.reporting_date).format('YYYY-MM-DD');

      let res;
      if (editDetails) {
        let filteredPayload = { ...payload, id: +editDetails?.reporting_id };
        res = await updateReportApi(filteredPayload);
      } else {
        res = await createReportApi(payload);
      }
      if (res?.statusCode === 200) {
        toast.success(editDetails ? 'Report updated successfully!' : 'Report added successfully!');
        handleList && handleList();
        onCancel();
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editDetails) {
      const time = {};

      if (editDetails?.billable || editDetails?.billable_mins) {
        const { hours, minutes } = convertToHoursAndMinutes(editDetails?.billable_mins);
        time.billable_hours = hours > 0 ? hours : '';
        time.billable_mins = minutes > 0 ? minutes : '';
      } else {
        time.billable_hours = '';
        time.billable_mins = '';
      }

      form.setFieldsValue({
        project_id: editDetails?.project?.id,
        description: editDetails?.description,
        checklists: editDetails?.checklists,
        billable: editDetails?.billable,
        reporting_date: dayjs(editDetails?.reporting_date),
        ...time
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ billable: true });
    }
  }, [editDetails, open]);

  return (
    <Modal
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      maskClosable={false}
      centered
      destroyOnClose
      prefixCls="antCustomModal"
      width={width}
      footer={false}>
      <Title style={{ textAlign: 'center' }}>
        {editDetails ? 'Edit Report' : 'Add New Report'}
      </Title>
      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          billable: true,
          reporting_date: dayjs()
        }}>
        {(value) => (
          <>
            <CreateFormWrapper>
              <FlexWrapperInput gap="10px" width="100%" wrap="nowrap" align="start">
                <FieldBox>
                  <label htmlFor="project_id">Project</label>
                  <Form.Item
                    name="project_id"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const isBillable = getFieldValue('billable') === true;
                          if (isBillable && !value) {
                            return Promise.reject(
                              new Error('Project is required when Billable is true')
                            );
                          }
                          return Promise.resolve();
                        }
                      })
                    ]}>
                    <Select
                      showSearch
                      className="customHeight"
                      allowClear
                      suffixIcon={<DropdownIconNew />}
                      placeholder="Select Option"
                      loading={projectLoading}
                      filterOption={(input, option) => {
                        option.label.toLowerCase().includes(input.toLowerCase());
                      }}
                      options={options}
                    />
                  </Form.Item>
                  {!editDetails &&
                    todaySubmittedReport?.length > 0 &&
                    todaySubmittedReport?.some(
                      (item) => item?.project_id === value?.project_id
                    ) && (
                      <GreyText color="red">
                        Report already exists. Submitting will overwrite it.
                      </GreyText>
                    )}
                </FieldBox>
                <FieldBox>
                  <label htmlFor="reporting_date">
                    Date <span>*</span>
                  </label>
                  <Form.Item
                    name="reporting_date"
                    type="text"
                    rules={[{ required: true, message: 'Reporting Date is required' }]}>
                    <DatePicker
                      className="customHeight"
                      format="DD MMM, YYYY"
                      placeholder="Select Date"
                      style={{ width: '100%' }}
                      suffixIcon={<LmsIcon />}
                      disabledDate={(current) => {
                        return (
                          !current.isSame(dayjs(), 'day') &&
                          !current.isSame(dayjs().subtract(1, 'day'), 'day')
                        );
                      }}
                    />
                  </Form.Item>
                </FieldBox>
              </FlexWrapperInput>

              <FlexWrapper justify="start" gap="16px">
                <label htmlFor="billable">Billable:</label>
                <Form.Item name="billable" rules={[{ required: false }]} style={{ margin: 0 }}>
                  <Radio.Group
                    onChange={() => form.setFieldsValue({ billable_hours: '', billable_mins: '' })}
                    prefixCls="antCustomRadio"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' }
                    ]}
                  />
                </Form.Item>
              </FlexWrapper>

              {form?.getFieldValue('billable') && (
                <GridContainer cols={2}>
                  <FieldBox>
                    <label htmlFor="billable_hours">Billing (in Hrs)</label>
                    <Form.Item
                      name="billable_hours"
                      dependencies={['billable']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const isBillable = getFieldValue('billable') === true;
                            if (!isBillable) {
                              return Promise.resolve();
                            }

                            if (value === undefined || value === '') {
                              return Promise.reject(new Error('Billing hours are required'));
                            }

                            if (!/^\d+$/.test(value)) {
                              return Promise.reject(new Error('Only numeric values are allowed'));
                            }

                            const numericValue = parseFloat(value);

                            if (numericValue > 24) {
                              return Promise.reject(new Error('Hours cannot be greater than 24'));
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}>
                      <Input
                        className="customHeight"
                        placeholder="Enter hours"
                        disabled={!form?.getFieldValue('billable')}
                        maxLength={2}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '24') {
                            form.setFieldValue('billable_mins', '');
                          }
                        }}
                      />
                    </Form.Item>
                  </FieldBox>
                  <FieldBox>
                    <label htmlFor="billable_hours">Billing (in Mins)</label>
                    <Form.Item
                      name="billable_mins"
                      dependencies={['billable']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const isBillable = getFieldValue('billable') === true;
                            if (!isBillable) {
                              return Promise.resolve();
                            }
                            if (value === undefined || value === '') {
                              return Promise.resolve();
                            }
                            if (!/^\d+$/.test(value)) {
                              return Promise.reject(new Error('Only numeric values are allowed'));
                            }
                            const numericValue = parseInt(value, 10);

                            if (numericValue > 59) {
                              return Promise.reject(new Error('Minutes cannot be greater than 59'));
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}>
                      <Input
                        className="customHeight"
                        placeholder="Enter minutes"
                        disabled={
                          !form?.getFieldValue('billable') ||
                          form?.getFieldValue('billable_hours') === '24'
                        }
                        maxLength={2}
                      />
                    </Form.Item>
                  </FieldBox>
                </GridContainer>
              )}

              <FieldBox>
                <FlexWrapper justify="space-between" width="100%">
                  <label htmlFor="description">
                    Description <span>*</span>
                  </label>
                  <GreyText>Max 300 words</GreyText>
                </FlexWrapper>
                <Form.Item
                  name="description"
                  rules={[
                    {
                      validator: () => {
                        const editor = quillRef.current?.getEditor();
                        const plainText = editor?.getText().trim();
                        const wordCount = plainText
                          ? plainText.split(/\s+/).filter(Boolean).length
                          : 0;

                        if (!plainText) {
                          return Promise.reject(new Error('Description is required'));
                        }

                        if (wordCount > 300) {
                          return Promise.reject(new Error('Description cannot exceed 300 words'));
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.getFieldValue('description')}
                    className="custom-quill"
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }]
                      ]
                    }}
                    onChange={(val) => {
                      const editor = quillRef.current?.getEditor();
                      const plainText = editor?.getText().trim();
                      const wordCount = plainText
                        ? plainText.split(/\s+/).filter(Boolean).length
                        : 0;

                      if (wordCount <= 300) {
                        form.setFieldValue('description', val);
                      }
                    }}
                    placeholder="Write Description here"
                  />
                </Form.Item>
              </FieldBox>

              <FlexWrapper direction="column" align="start" gap="10px">
                <label htmlFor="checklists" className="points-title">
                  Done Points
                </label>
                <Form.Item
                  name="checklists"
                  rules={[{ required: false }]}
                  style={{ width: '100%' }}>
                  <Checkbox.Group
                    className="antCustomCheckboxing"
                    prefixCls="antCustomCheckbox"
                    options={reportingDonePoints}></Checkbox.Group>
                </Form.Item>
              </FlexWrapper>
              <FlexWrapper justify="end">
                <Button
                  loading={loading}
                  prefixCls="antCustomBtn"
                  htmlType="submit"
                  style={{ width: '150px', height: '42px' }}>
                  {editDetails ? 'Save' : 'Submit'}
                </Button>
              </FlexWrapper>
            </CreateFormWrapper>
          </>
        )}
      </Form>
    </Modal>
  );
};

CreateReport.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  editDetails: PropTypes.object,
  handleList: PropTypes.func
};

export default CreateReport;

export const CreateFormWrapper = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;

  .points-title {
    font-family: 'Plus Jakarta Sans';
    font-weight: 500;
    font-size: 16px;
    color: #0e0e0e;
    margin: 0;
    @media (max-width: 768px) {
      width: 100%;
      display: flex;
      justify-content: start;
      align-items: start;
    }
  }

  .team-sub-title {
    font-family: 'Plus Jakarta Sans';
    font-weight: 400;
    font-size: 16px;
    color: #0e0e0e;
    margin: 0;
  }
  .antCustomCheckboxing {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px 10px;
    width: 100%;
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 12px;
      .points-title {
        width: 100%;
        display: flex;
        justify-content: start;
        align-items: start;
      }
    }
  }
`;
export const FlexWrapperInput = styled.div`
  width: ${(props) => props.width || '100%'};
  display: grid;
  grid-template-columns: 70% 30%;
  gap: ${(props) => props.gap || '10px'};
  cursor: ${(props) => props.cursor || 'auto'};
  margin: ${(props) => props.margin || '0px'};
  align-items: ${(props) => props.align || 'center'};
  justify-content: ${(props) => props.justify || 'start'};

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;
export const GridContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};
  gap: ${({ gap }) => gap || '16px'};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
